import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Upload, X } from 'lucide-react';
import api from '../../services/api';
import PermitMap from '../../components/PermitMap';
import { useAuth } from '../../contexts/AuthContext';

const PermitCreate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [landRegistryDocument, setLandRegistryDocument] = useState(null);
  const [registryMapDocument, setRegistryMapDocument] = useState(null);
  const [files, setFiles] = useState([]);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [boundaryPoints, setBoundaryPoints] = useState([]);

  // Check if user has a subdivisionId set - if yes, lock the dropdown
  const hasSubdivision = user?.subdivisionId && user.subdivisionId !== '';
  const isSubdivisionLocked = !!hasSubdivision;

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({
    defaultValues: {
      subdivisionId: hasSubdivision ? user.subdivisionId.toString() : ''
    }
  });

  // Fetch subdivisions
  const { data: subdivisionsData, isLoading: isLoadingSubdivisions, error: subdivisionsError } = useQuery(
    'subdivisions',
    async () => {
      const response = await api.get('/subdivisions');
      const subdivisions = response.data?.subdivisions;
      if (!Array.isArray(subdivisions)) {
        console.error('Subdivisions data is not an array:', subdivisions);
        return [];
      }
      return subdivisions;
    },
    {
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes (300000ms)
      cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    }
  );

  // Ensure subdivisionsData is always an array to prevent .map errors
  const subdivisions = Array.isArray(subdivisionsData) ? subdivisionsData : [];

  // Set subdivision value when subdivisions load (handles navigation case)
  useEffect(() => {
    if (hasSubdivision && subdivisions.length > 0 && !isLoadingSubdivisions) {
      setValue('subdivisionId', user.subdivisionId.toString(), { shouldValidate: true });
    }
  }, [hasSubdivision, user?.subdivisionId, subdivisions.length, isLoadingSubdivisions, setValue, subdivisions]);

  // Set default validity date to 1 year from today
  useEffect(() => {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    const defaultDate = oneYearFromNow.toISOString().split('T')[0];
    setValue('validity_date', defaultDate);
  }, [setValue]);

  const createPermit = useMutation(
    async (data) => {
      const formData = new FormData();

      // Add text fields
      Object.keys(data).forEach(key => {
        if (data[key]) {
          formData.append(key, data[key]);
        }
      });

      // Add boundary points if set (minimum 3 points)
      if (boundaryPoints && boundaryPoints.length >= 3) {
        formData.append('boundaryPoints', JSON.stringify(boundaryPoints));
      } else if (location.lat && location.lng) {
        // Fall back to single location if no boundary points
        formData.append('latitude', location.lat);
        formData.append('longitude', location.lng);
      }

      // Add land registry document (optional)
      if (landRegistryDocument) {
        formData.append('landRegistryDocument', landRegistryDocument);
      }

      // Add registry map document (optional)
      if (registryMapDocument) {
        formData.append('registryMapDocument', registryMapDocument);
      }

      // Add supporting documents (optional)
      files.forEach(file => {
        formData.append('documents', file);
      });

      const response = await api.post('/permits', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Permit created successfully!');
        navigate(`/permits/${data.permit.id}`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create permit');
      },
    }
  );

  const handleLandRegistryChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLandRegistryDocument(file);
    }
  };

  const removeLandRegistryDocument = () => {
    setLandRegistryDocument(null);
  };

  const handleRegistryMapChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setRegistryMapDocument(file);
    }
  };

  const removeRegistryMapDocument = () => {
    setRegistryMapDocument(null);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = (data) => {
    // Validate boundary points only if some are provided (must be 0 or 3+)
    if (boundaryPoints && boundaryPoints.length > 0 && boundaryPoints.length < 3) {
      toast.error('If adding boundary points, please add at least 3 points');
      return;
    }
    createPermit.mutate(data);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/permits')}
          className="flex items-center text-secondary-600 hover:text-secondary-900 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Permits
        </button>
        <h1 className="text-2xl font-bold text-secondary-900">Create New Permit</h1>
        <p className="text-secondary-600">Fill in the details for the new land permit application</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Permit Number *</label>
              <input
                {...register('permit_no', { required: 'Permit number is required' })}
                type="text"
                className={`input ${errors.permit_no ? 'input-error' : ''}`}
                placeholder="Enter permit number (e.g., 123/MON/25)"
              />
              {errors.permit_no && (
                <p className="mt-1 text-sm text-red-600">{errors.permit_no.message}</p>
              )}
            </div>

            <div>
              <label className="label">Permit Holder Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                className={`input ${errors.name ? 'input-error' : ''}`}
                placeholder="Enter full name of permit holder"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="label">Father's Name</label>
              <input
                {...register('father_name')}
                type="text"
                className="input"
                placeholder="S/O: Father's name"
              />
            </div>

            <div>
              <label className="label">Village/Ward *</label>
              <input
                {...register('village', { required: 'Village/Ward is required' })}
                type="text"
                className={`input ${errors.village ? 'input-error' : ''}`}
                placeholder="Enter village or ward name"
              />
              {errors.village && (
                <p className="mt-1 text-sm text-red-600">{errors.village.message}</p>
              )}
            </div>

            <div>
              <label className="label">Subdivision *</label>
              <select
                {...register('subdivisionId', { required: 'Subdivision is required' })}
                className={`input ${errors.subdivisionId ? 'input-error' : ''} ${(isSubdivisionLocked || isLoadingSubdivisions) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                disabled={isSubdivisionLocked || isLoadingSubdivisions}
              >
                <option value="">
                  {isLoadingSubdivisions ? 'Loading subdivisions...' : 'Select Subdivision'}
                </option>
                {subdivisions.map((subdivision) => (
                  <option key={subdivision.id} value={subdivision.id}>
                    {subdivision.name}
                  </option>
                ))}
              </select>
              {isSubdivisionLocked && !isLoadingSubdivisions && (
                <p className="mt-1 text-sm text-gray-600">
                  <span className="font-medium">Note:</span> You can only create permits in this location only
                </p>
              )}
              {subdivisionsError && !isSubdivisionLocked && (
                <p className="mt-1 text-sm text-red-600">Failed to load subdivisions. Please refresh the page.</p>
              )}
              {errors.subdivisionId && !isSubdivisionLocked && (
                <p className="mt-1 text-sm text-red-600">{errors.subdivisionId.message}</p>
              )}
            </div>

            <div>
              <label className="label">Post Office</label>
              <input
                {...register('post_office')}
                type="text"
                className="input"
                placeholder="Enter post office"
              />
            </div>

            <div>
              <label className="label">Police Station</label>
              <input
                {...register('police_station')}
                type="text"
                className="input"
                placeholder="Enter police station"
              />
            </div>

            <div>
              <label className="label">District</label>
              <input
                {...register('district')}
                type="text"
                className="input"
                placeholder="District"
                defaultValue="MON"
              />
            </div>

            <div>
              <label className="label">Size (sq ft) *</label>
              <input
                {...register('size_sqft', { required: 'Size is required' })}
                type="text"
                className={`input ${errors.size_sqft ? 'input-error' : ''}`}
                placeholder="e.g., 55x30=1655 SQ FT"
              />
              {errors.size_sqft && (
                <p className="mt-1 text-sm text-red-600">{errors.size_sqft.message}</p>
              )}
            </div>

            <div>
              <label className="label">Validity Date</label>
              <input
                {...register('validity_date')}
                type="date"
                className="input"
              />
            </div>
          </div>
        </div>

        {/* Land Details */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Land Boundaries</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">North Boundary</label>
              <input
                {...register('north_boundary')}
                type="text"
                className="input"
                placeholder="e.g., QUARTER"
              />
            </div>

            <div>
              <label className="label">South Boundary</label>
              <input
                {...register('south_boundary')}
                type="text"
                className="input"
                placeholder="e.g., SHAMONG"
              />
            </div>

            <div>
              <label className="label">East Boundary</label>
              <input
                {...register('east_boundary')}
                type="text"
                className="input"
                placeholder="e.g., MANWANG"
              />
            </div>

            <div>
              <label className="label">West Boundary</label>
              <input
                {...register('west_boundary')}
                type="text"
                className="input"
                placeholder="e.g., WENGAO"
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">Location Description</label>
              <textarea
                {...register('location_description')}
                rows={3}
                className="input"
                placeholder="e.g., North: Soil Office, South: CS Office..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">Remarks</label>
              <textarea
                {...register('remarks')}
                rows={3}
                className="input"
                placeholder="Any additional remarks or notes"
              />
            </div>

            {/* Location Map */}
            <div className="md:col-span-2">
              <label className="label">Set Permit Boundary (Click to add minimum 3 points)</label>
              <p className="text-sm text-gray-600 mb-2">
                Click on the map to add boundary points. Minimum 3 points required to create a polygon boundary.
              </p>
              <PermitMap
                latitude={location.lat}
                longitude={location.lng}
                boundaryPoints={boundaryPoints}
                onChange={(newLocation) => setLocation(newLocation)}
                onBoundaryChange={(points) => setBoundaryPoints(points)}
                editable={true}
                height="400px"
              />
              {boundaryPoints.length > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {boundaryPoints.length} point(s) added {boundaryPoints.length >= 3 ? '✓' : `(${3 - boundaryPoints.length} more needed)`}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Documents Upload Section */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-secondary-900 mb-4">Documents</h3>

          {/* Land Registry and Registry Map - Compact Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Land Registry Document */}
            <div>
              <label className="label">Land Registry <span className="text-secondary-500">(Optional)</span></label>
              <div className="border-2 border-dashed border-secondary-300 rounded-lg p-3 bg-secondary-50">
                <label className="cursor-pointer flex items-center justify-center">
                  <Upload className="h-5 w-5 text-secondary-600 mr-2" />
                  <span className="text-sm text-secondary-700">Choose File</span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleLandRegistryChange}
                    className="sr-only"
                  />
                </label>
              </div>
              {landRegistryDocument && (
                <div className="mt-2 flex items-center justify-between bg-primary-50 p-2 rounded-md border border-primary-200">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-secondary-900 truncate block">{landRegistryDocument.name}</span>
                    <span className="text-xs text-secondary-500">
                      ({(landRegistryDocument.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={removeLandRegistryDocument}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Registry Map Document */}
            <div>
              <label className="label">Registry Map <span className="text-secondary-500">(Optional)</span></label>
              <div className="border-2 border-dashed border-secondary-300 rounded-lg p-3 bg-secondary-50">
                <label className="cursor-pointer flex items-center justify-center">
                  <Upload className="h-5 w-5 text-secondary-600 mr-2" />
                  <span className="text-sm text-secondary-700">Choose File</span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleRegistryMapChange}
                    className="sr-only"
                  />
                </label>
              </div>
              {registryMapDocument && (
                <div className="mt-2 flex items-center justify-between bg-primary-50 p-2 rounded-md border border-primary-200">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-secondary-900 truncate block">{registryMapDocument.name}</span>
                    <span className="text-xs text-secondary-500">
                      ({(registryMapDocument.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={removeRegistryMapDocument}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Supporting Documents Section */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-secondary-900">Supporting Documents <span className="text-secondary-500">(Optional)</span></h4>
              <label className="cursor-pointer">
                <span className="inline-flex items-center px-3 py-2 border border-primary-600 text-sm font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50">
                  <Upload className="h-4 w-4 mr-2" />
                  + Attach more document
                </span>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="sr-only"
                />
              </label>
            </div>

            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-secondary-50 p-2 rounded-md border border-secondary-200">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-secondary-900 truncate block">{file.name}</span>
                      <span className="text-xs text-secondary-500">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="ml-2 text-red-600 hover:text-red-800"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/permits')}
            className="btn btn-outline"
            disabled={createPermit.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={createPermit.isLoading}
          >
            {createPermit.isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </div>
            ) : (
              'Create Permit'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PermitCreate;